import { setNodeUploading, useNodeAwareness } from '@/entities/node/lib/node-awareness'
import { useFlowPermission } from '@/shared/config/flow-permission-store'
import { assetUploadStore, cancelUploadTask } from '@/shared/lib/asset-upload-store'
import { useSelectionDragging } from '@/shared/lib/pointer-pressed-store'
import type { BaseNodeData } from '@/shared/lib/node-registry'
import { cn } from '@/shared/lib/utils'
import { getUserColor } from '@/shared/lib/user-color'
import { type Node, type NodeProps, useStore } from '@xyflow/react'
import { memo, type CSSProperties, type ReactNode, useRef, useState, createContext, useContext } from 'react'
import { useSnapshot } from 'valtio'
import { readNodeCustomDataSnapshot, useCustomNodeData } from '../lib'
import { MagneticConnectionHandles } from '../magnetic-handles'
import { InlineEditableNodeTitle } from './InlineEditableNodeTitle'
import { InternalHandle } from "@/entities/node/magnetic-handles/lib/internal-handle.tsx"
import qlWatermarkImg from '@/assets/images/ai_watermark.png'
// FSD 架构例外说明：本项目将 entities/flow 视为偏向 shared 层的画布基建。
// 故允许 entities/node 单向依赖 entities/flow 的操作方法，避免过度封装导致代码追踪困难。
import { getOp } from "@/shared/lib/op-registry"
import { aiWatermarkPreferenceStore } from '@/entities/team'
import { canvasAssetsStore, readAssetRefsValue } from '@/entities/node/canvas-asset'

function NodeWatermark({ nodeType, op, assetRefs }: { nodeType?: string; op?: string; assetRefs?: unknown }) {
  const { serverAiWatermark } = useSnapshot(aiWatermarkPreferenceStore)

  if (serverAiWatermark === 0) {
    return null
  }

  if (nodeType !== 'image' && nodeType !== 'video') {
    return null
  }

  if (op === 'material') {
    return null
  }

  const refs = readAssetRefsValue(assetRefs)
  if (refs.length === 0) {
    return null
  }

  const assetId = refs[0]
  const asset = canvasAssetsStore.assets.get(assetId)
  if (!asset || !(asset as any).material_id) {
    return null
  }

  return (
    <img src={qlWatermarkImg} alt="watermark" className="absolute top-2 left-2 z-10 w-[32px] opacity-60 pointer-events-none select-none" draggable={false} />
  )
}

interface NodeFrameProps {
  titleIcon?: ReactNode
  titleBadge?: ReactNode
  titleMeta?: ReactNode
  props: NodeProps
  frameClassName?: string
  frameStyle?: CSSProperties
  frameOverlay?: ReactNode
  children: ReactNode
  onDoubleClick?: (event: React.MouseEvent<HTMLDivElement>) => void
  hideTitle?: boolean
}

// 判断节点是否属于某个祖先 group 的组任务；嵌套组任务下不能只看直接 parentId。
function hasGroupTaskAncestor(
  currentNode: Node | undefined,
  nodeMap: Map<string, Node>,
  runId: unknown,
) {
  if (!currentNode || typeof runId !== 'string' || runId.length === 0) {
    return false
  }

  const visited = new Set<string>()
  let parentId = currentNode.parentId

  while (parentId) {
    if (visited.has(parentId)) return false

    visited.add(parentId)
    if (readNodeCustomDataSnapshot<BaseNodeData>(parentId).runId === runId) {
      return true
    }

    parentId = nodeMap.get(parentId)?.parentId
  }

  return false
}

export const NodeHoverContext = createContext<boolean>(false)
export const useNodeHoverContext = () => useContext(NodeHoverContext)

export const NodeFrame = memo(function NodeFrame({
  titleIcon,
  titleBadge,
  titleMeta,
  props,
  frameClassName,
  frameStyle,
  frameOverlay,
  children,
  onDoubleClick,
  hideTitle = false,
}: NodeFrameProps) {
  const nodeId = props.id
  const nodeRef = useRef<HTMLDivElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const nodeState = useNodeAwareness(nodeId || '')
  const isUploading = !!nodeState.isUploading
  const isEditing = !!nodeState.isEditing
  const uploadSnap = useSnapshot(assetUploadStore)
  const uploadingTask = uploadSnap.uploadTasks.find(
    (t) => t.nodeId === nodeId && t.status === 'uploading'
  )
  const uploadProgress = uploadingTask ? Math.round(uploadingTask.progress) : null
  const { snapshot } = useCustomNodeData<BaseNodeData>()

  const handleCancelUpload = (event: React.MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    if (uploadingTask) {
      cancelUploadTask(uploadingTask.id)
      setNodeUploading(nodeId || '', false)
    }
  }

  const zoom = useStore((s) => s.transform[2])
  const showTitle = !hideTitle && zoom >= 0.3

  const nodes = useStore((s) => s.nodes)
  const selectedCount = useStore((s) => s.nodes.reduce((count, node) => count + (node.selected ? 1 : 0), 0))
  const currentNode = nodes.find((node) => node.id === nodeId)
  const nodeMap = new Map(nodes.map((node) => [node.id, node]))
  const status = snapshot.runId ? snapshot.status : undefined
  const isGroupTaskChild = hasGroupTaskAncestor(currentNode, nodeMap, snapshot.runId)
  const opMeta = snapshot.op ? getOp(snapshot.op) : undefined
  const isMaterialNode = !opMeta?.acceptSourceTypes
  const groupTaskBorderClass = !props.selected && isGroupTaskChild
    ? status === 'pending'
      ? 'border-yellow-500/80 ring-1 ring-yellow-500/40'
      : status === 'running'
        ? 'border-blue-500/80 ring-1 ring-blue-500/40'
        : status === 'failed'
          ? 'border-red-500/80 ring-1 ring-red-500/40'
          : undefined
    : undefined

  // const { isReadonly } = useFlowPermission()
  const selectionDragging = useSelectionDragging()

  return (
    <div
      ref={nodeRef}
      className={cn(
        'group/node-frame relative select-none overflow-visible rounded-2xl border border-white/10 bg-[#17181c]',
        frameClassName,
        groupTaskBorderClass,
        props.selected ? 'border-accent ring-1 ring-accent' : (isEditing && 'ring-1'),
      )}
      style={{
        ...frameStyle,
        ...(isEditing && !props.selected && nodeState.editorUid
          ? { borderColor: getUserColor(`${nodeState.editorUid}-${nodeState.editorClientId}`), '--tw-ring-color': getUserColor(`${nodeState.editorUid}-${nodeState.editorClientId}`) } as React.CSSProperties
          : isEditing && !props.selected ? { borderColor: '#E04E44', '--tw-ring-color': '#E04E44' } as React.CSSProperties : {}),
      }}
      onDoubleClick={onDoubleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {showTitle && (
        <div className="absolute -top-7 left-0 flex w-full max-w-full items-center px-0.5">
          <InlineEditableNodeTitle icon={titleIcon} />
          {titleBadge}
          {titleMeta}
        </div>
      )}
      {isEditing && !props.selected && (
        <div
          className="absolute -top-6 right-1 z-50 pointer-events-none flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium text-white shadow-sm backdrop-blur-sm"
          style={{ backgroundColor: nodeState.editorUid ? `${getUserColor(`${nodeState.editorUid}-${nodeState.editorClientId}`)}B3` : '#E04E44B3' }}
        >
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
          编辑中...
        </div>
      )}
      {/*默认手柄*/}
      {/* {isReadonly && <InternalHandle side={"left"} />} */}
      {/*磁吸手柄：框选拖动期间或选中多个节点时隐藏 plus 图标和圆圈，保留 handle 以支持批量连接吸附*/}
      {<MagneticConnectionHandles props={props} zoom={zoom} nodeRef={nodeRef} isHovered={isHovered} hideLeft={isMaterialNode} showPlus={!selectionDragging && selectedCount <= 1} hideOverlay={selectedCount > 1} />}
      {/*<SimpleConnectionHandles isHovered={isHovered && !isPreview} />*/}
      <NodeHoverContext.Provider value={isHovered}>
        <div className="relative h-full overflow-hidden rounded-2xl">
          {zoom >= 0.3 && <NodeWatermark nodeType={props.type} op={snapshot.op} assetRefs={snapshot.assetRefs} />}
          {children}

          {/* 上传中状态遮罩 */}
          {isUploading && (
            <div className="absolute inset-0 z-100 flex flex-col items-center justify-center gap-3 bg-[#1F1F1F] pt-2 text-[12px] font-medium text-[#D0D0D0] drop-shadow-md backdrop-blur-[2px]">
              <span>{uploadProgress === null ? '正在上传素材...' : `正在上传素材...${uploadProgress}%`}</span>
              {uploadingTask && (
                <button
                  type="button"
                  onClick={handleCancelUpload}
                  className="rounded-full bg-white/10 px-4 py-1 text-[12px] text-[#D0D0D0] transition hover:bg-white/20"
                >
                  取消
                </button>
              )}
            </div>
          )}

        </div>
        {frameOverlay}
      </NodeHoverContext.Provider>
      {/*默认手柄*/}
      {/* {isReadonly && <InternalHandle side={"right"} />} */}
    </div>
  )
})
